<template>
  <v-flex class="pl-2 pr-2 pb-2">
    <Generic_Translations
      v-if="openCategoryTranslations"
      :columns="categoryColumns"
      :translations="categoryTranslations"
      @closeTranslations="closeCategoryTranslations"
      @saveTranslations="saveCategoryTranslations"
    ></Generic_Translations>
    <v-layout align-start justify-start row class="mb-4">
      <v-icon color="primary" style="font-size: 50px">category</v-icon>
      <h2 :style="`color: ${$vuetify.theme.primary}`" class="mt-2">
        {{ $translate("CATEGORIES") }}
      </h2>
      <v-spacer></v-spacer>
      <v-btn fab right color="primary" @click="addNewCategory">
        <v-icon>add</v-icon>
      </v-btn>
    </v-layout>

    <v-card>
      <v-card-title class="secondary white--text headline">
        {{ $translate("CATEGORIES") }}
        <v-spacer></v-spacer>
        <v-btn round fab color="primary" @click="getCategoryTranslations">
          <v-icon dark>language</v-icon>
        </v-btn>
      </v-card-title>
      <v-layout justify-space-between pa-3>
        <v-flex xs5>
          <v-treeview
            :active.sync="categoriesActive"
            :open.sync="categoriesOpen"
            :items="categories"
            item-key="UniqueId"
            item-text="Name"
            item-children="SubCategories"
            activatable
            active-class="primary--text"
            return-object
            transition
            style="text-align: left"
          >
          </v-treeview>
        </v-flex>

        <v-flex v-if="editMode" d-flex text-xs-center>
          <v-form
            ref="categoryForm"
            v-model="categoryFormValid"
            lazy-validation
          >
            <v-text-field
              v-model="categoryForm.Name"
              :label="$translate('CATEGORY_NAME')"
              required
              :rules="categoryNameRule"
              @change="saveBtnActive = true"
            ></v-text-field>

            <v-text-field
              v-model="categoryForm.Ranking"
              :label="$translate('CATEGORY_RANKING')"
              required
              type="number"
              :rules="categoryRankingeRule"
              @change="saveBtnActive = true"
            ></v-text-field>

            <v-select
              v-model="categoryForm.MainCategoryInterestId"
              :items="categories"
              item-text="Name"
              item-value="InterestId"
              label="Parent of category"
              @change="saveBtnActive = true"
            ></v-select>

            <v-checkbox
              v-model="categoryForm.ForProduct"
              label="Used for product"
            ></v-checkbox>
            <v-checkbox
              v-model="categoryForm.ForUser"
              label="Used for user"
            ></v-checkbox>

            <v-checkbox
              v-model="categoryForm.ForEvent"
              label="Used for event"
              @change="saveBtnActive = true"
            ></v-checkbox>

            <v-checkbox
              v-model="categoryForm.ForLocation"
              label="Used for location"
              @change="saveBtnActive = true"
            ></v-checkbox>

            <v-checkbox
              v-model="categoryForm.InSearchEngine"
              label="Used in search engine"
              @change="saveBtnActive = true"
            ></v-checkbox>

            <v-checkbox
              v-model="categoryForm.DefaultSelectedInSearchEngine"
              label="Default delected in search engine"
              @change="saveBtnActive = true"
            ></v-checkbox>

            <v-btn color="secondary" @click="cancelEdit">{{
              $translate("CANCEL")
            }}</v-btn>

            <v-btn
              color="primary"
              :disabled="!saveBtnActive"
              @click="saveCategory"
              >{{ $translate("SAVE") }}</v-btn
            >
          </v-form>
        </v-flex>

        <v-flex v-else d-flex text-xs-center>
          <v-scroll-y-transition mode="out-in">
            <div
              v-if="categoriesActive.length === 0"
              class="title grey--text text--lighten-1 font-weight-light"
              style="align-self: center"
            >
              Select a category
            </div>
            <v-card
              v-else
              :key="categorySelected.UniqueId"
              class="pt-4 mx-auto"
              flat
              max-width="500"
            >
              <v-card-text>
                <h3 class="headline mb-2">
                  {{ categorySelected.Name }}
                </h3>
              </v-card-text>
              <v-divider></v-divider>
              <v-layout tag="v-card-text" text-xs-left column wrap>
                <v-flex>
                  <v-layout row wrap>
                    <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2
                      >Parent:</v-flex
                    >
                    <v-flex>{{ parentOfSelectedCategory.Name }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout row wrap>
                    <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2
                      >Ranking:</v-flex
                    >
                    <v-flex>{{ categorySelected.Ranking }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout row wrap>
                    <v-flex tag="strong" xs5 text-xs-right mr-3 mb-1
                      >Used For:</v-flex
                    >
                    <v-flex>{{ categoryUsedFor }}</v-flex>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout row wrap>
                    <v-flex tag="strong" xs5 text-xs-right mr-3 mb-1
                      >Used In Search Engine:</v-flex
                    >
                    <v-checkbox
                      v-model="categorySelected.InSearchEngine"
                      disable
                    ></v-checkbox>
                  </v-layout>
                </v-flex>

                <v-flex>
                  <v-layout row wrap>
                    <v-flex tag="strong" xs5 text-xs-right mr-3 mb-2
                      >Default Selected In Search Engine:</v-flex
                    >
                    <v-checkbox
                      v-model="categorySelected.DefaultSelectedInSearchEngine"
                      disable
                    ></v-checkbox>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-card-actions>
                <v-btn color="secondary" @click="enableDeleteDialog">
                  <v-icon dark>delete</v-icon>
                </v-btn>
                <v-btn color="primary" @click="enableEditMode">
                  <v-icon dark>edit</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-scroll-y-transition>
        </v-flex>
      </v-layout>
    </v-card>

    <v-dialog v-model="deleteDialogActive" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $translate("DELETE_CATEGORY") }}</span>
        </v-card-title>

        <v-card-text>
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                disabled
                v-model="categorySelected.Name"
                :label="$translate('CATEGORY_NAME')"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary darken-1"
            round
            @click="deleteDialogActive = false"
            >{{ $translate("CANCEL") }}</v-btn
          >
          <v-btn color="primary darken-1" round @click="deleteCategory">{{
            $translate("DELETE")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import * as dssRequest from "@/api/dssRequest";
import CategoryObject from "@/objects/CategoryObject";
import Generic_Translations from "../Generic_Translations";
import Location_Batchgroups from "../Locations_Batchgroups";

export default {
  name: "AccountCategories",
  components: {
    // Importing the components that are used within this component
    Generic_Translations
  },
  data() {
    return {
      categoryTranslations: {},
      categoryColumns: [],
      openCategoryTranslations: false,
      selectedCategoryForTranslation: {},
      categories: [],
      categoriesOpen: [],
      categoriesActive: [],
      currentInterestId: "",
      categoryForm: {},
      Picture: "",
      PictureUrl: "",
      categoryFormValid: false,
      categoryNameRule: [v => !!v || "A name for the category is required"],
      categoryRankingeRule: [
        v => !!v || "A ranking for the category is required"
      ],
      idPostfix: ":Name=",
      editMode: false,
      deleteDialogActive: false,
      usedForNone: "None",
      usedForUser: "User",
      usedForEvent: "Event",
      usedForLocation: "Location",
      usedForProduct: "Product",
      saveBtnActive: false
    };
  },
  computed: {
    categorySelected() {
      if (!this.categoriesActive.length) return {};

      const lastSelected = this.categoriesActive[0];
      this.currentInterestId = lastSelected.InterestId;
      return lastSelected;
    },
    parentOfSelectedCategory() {
      let parent = {};
      parent = this.categories.find(
        category =>
          category.InterestId === this.categorySelected.MainCategoryInterestId
      );

      if (parent === undefined) {
        return {};
      } else {
        return parent;
      }
    },
    categoryUsedFor() {
      let usedFor = "";
      if (this.categorySelected.ForProduct) {
        usedFor = this.usedForProduct;
      }
      if (this.categorySelected.ForUser) {
        usedFor = this.usedForUser;
      }
      if (this.categorySelected.ForEvent) {
        if (usedFor === "") {
          usedFor = this.usedForEvent;
        } else {
          usedFor += ", " + this.usedForEvent;
        }
      }
      if (this.categorySelected.ForLocation) {
        if (usedFor === "") {
          usedFor = this.usedForLocation;
        } else {
          usedFor += ", " + this.usedForLocation;
        }
      }
      if (usedFor === "") {
        usedFor = this.usedForNone;
      }

      return usedFor;
    }
  },
  mounted() {
    this.getCategories();
  },
  methods: {
    closeCategoryTranslations() {
      this.openCategoryTranslations = false;
    },
    saveCategoryTranslations(translationData) {
      let payload = {};
      payload.Translations = translationData;
      dssRequest.doRequest(
        response => {
          console.log(
            "The call for saveCategoryTranslations on - Account_Categories.vue - was successful",
            response
          );
          this.openCategoryTranslations = false;
        },
        error => {
          console.log(
            "The call for saveTranslations on - Account_categories.vue - was not successful",
            error
          );
          window.EventBus.fire(
            "activateSnackBar",
            "Something went wrong while saving the translations, please try again."
          );
        },
        "saveCategoryTranslations",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    getCategoryTranslations() {
      const payload = {};
      dssRequest.doRequest(
        response => {
          console.log(
            "The call getCategoryTranslations was successful",
            response
          );
          this.CategoryTranslations = {};
          this.categoryColumns = [];
          this.categoryTranslations = response.data.Translations;
          this.categoryColumns = response.data.ColumnNames;
          this.openCategoryTranslations = true;
          console.log("Brian this.categoryColumns", this.categoryColumns);
        },
        error => {
          console.log(
            "The call getCategoryTranslations was not successful",
            error
          );
        },
        "getCategoryTranslations",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    getCategories() {
      const payload = {
        LanguageTagId: this.$store.getters.getUserLanguage
      };

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [getInterestsForAccount] on [LocationsNode_ISE] in component [Account_Categories] succeeded",
            response
          );
          this.categories = [];
          if (
            response.data.hasOwnProperty("Categories") &&
            response.data.Categories !== undefined
          ) {
            this.categories = [...response.data.Categories];
            this.generateUniqueId();

            // If currentInterestId is not empty, make this category active
            let foundCategory = false;
            if (
              this.currentInterestId !== undefined &&
              this.currentInterestId !== ""
            ) {
              for (let category of this.categories) {
                if (category.InterestId === this.currentInterestId) {
                  let categoryTmp = Object.assign({}, category);
                  this.categoriesActive = [];
                  this.categoriesActive.push(categoryTmp);
                  foundCategory = true;
                } else {
                  if (
                    category.hasOwnProperty("SubCategories") &&
                    category.SubCategories.length > 0
                  ) {
                    for (let subCategory of category.SubCategories) {
                      if (subCategory.InterestId === this.currentInterestId) {
                        let subCategoryTmp = Object.assign({}, subCategory);
                        this.categoriesActive = [];
                        this.categoriesActive.push(subCategoryTmp);
                        foundCategory = true;
                        break;
                      }
                    }
                  }
                }

                if (foundCategory) break;
              }
            }
          }
        },
        error => {
          console.log(
            "The call [getInterestsForAccount] on [LocationsNode_ISE] in component [Account_Categories] failed",
            error
          );
        },
        "getInterestsForAccount",
        "LocationsNode_ISE",
        payload
      );
    },
    generateUniqueId() {
      for (let category of this.categories) {
        let mainCompositeId =
          category.InterestId + this.idPostfix + category.Name;
        category.UniqueId = mainCompositeId;
        if (
          category.hasOwnProperty("SubCategories") &&
          category.SubCategories.length > 0
        ) {
          for (let subCategory of category.SubCategories) {
            let subCompositeId =
              subCategory.InterestId + this.idPostfix + subCategory.Name;
            subCategory.UniqueId = subCompositeId;
          }
        }
      }
    },
    addNewCategory() {
      this.categoryForm = Object.assign({}, CategoryObject.initCategory());
      this.saveBtnActive = false;
      this.editMode = true;
    },
    enableEditMode() {
      this.categoryForm = Object.assign({}, this.categorySelected);
      this.saveBtnActive = false;
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
    },
    saveCategory() {
      if (!this.$refs.categoryForm.validate()) return;

      this.currentInterestId = "";

      const payload = {
        InterestId: this.categoryForm.InterestId,
        Name: this.categoryForm.Name,
        AccountId: this.$store.getters.getCurrentAccountId,
        MainCategoryInterestId: this.categoryForm.MainCategoryInterestId,
        ForProduct: this.categoryForm.ForProduct,
        ForUser: this.categoryForm.ForUser,
        ForEvent: this.categoryForm.ForEvent,
        ForLocation: this.categoryForm.ForLocation,
        Ranking: this.categoryForm.Ranking,
        Picture: this.categoryForm.Picture,
        PictureUrl: this.categoryForm.PictureUrl,
        InSearchEngine: this.categoryForm.InSearchEngine,
        DefaultSelectedInSearchEngine: this.categoryForm
          .DefaultSelectedInSearchEngine
      };

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [addInterest] on [LocationsNode_ISE] in component [Account_Categories] succeeded",
            response
          );

          this.currentInterestId = response.data.InterestId;

          this.editMode = false;
          this.getCategories();
        },
        error => {
          console.log(
            "The call [addInterest] on [LocationsNode_ISE] in component [Account_Categories] failed",
            error
          );
        },
        "addInterest",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    },
    enableDeleteDialog() {
      this.deleteDialogActive = true;
    },
    deleteCategory() {
      const payload = {
        InterestId: this.categorySelected.InterestId
      };

      dssRequest.doRequest(
        response => {
          console.log(
            "The call [removeInterest] on [LocationsNode_ISE] in component [Account_Categories] succeeded",
            response
          );

          this.deleteDialogActive = false;
          this.currentInterestId = "";

          this.getCategories();
        },
        error => {
          console.log(
            "The call [removeInterest] on [LocationsNode_ISE] in component [Account_Categories] failed",
            error
          );
        },
        "removeInterest",
        this.$store.getters.getCurrentAccountId,
        payload
      );
    }
  }
};
</script>

<style scoped></style>
